import { XIcon } from '@phosphor-icons/react';
import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './Modal.module.css';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** Заголовок связывается с диалогом через aria-labelledby. */
  labelledBy: string;
  describedBy?: string | undefined;
  children: ReactNode;
  /** Закрытие недоступно, пока идёт необратимая операция. */
  dismissible?: boolean | undefined;
  className?: string | undefined;
}

/**
 * Модальное окно на нативном `<dialog>`.
 *
 * Почему нативный элемент, а не div с role="dialog": браузер сам даёт top layer
 * (никаких войн z-index с шапкой), настоящую ловушку фокуса, `::backdrop` и
 * закрытие по Esc. Своя реализация всего этого — источник ошибок доступности.
 *
 * Закрытие всегда идёт через `onClose`, а не через `dialog.close()` напрямую:
 * состояние остаётся у React, а `useEffect` только синхронизирует с ним DOM.
 */
export function Modal({
  open,
  onClose,
  labelledBy,
  describedBy,
  children,
  dismissible = true,
  className,
}: ModalProps) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  // Прокрутка фона блокируется, пока окно открыто: иначе страница уезжает
  // под диалогом при скролле на мобильных.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      className={[styles.dialog, className].filter(Boolean).join(' ')}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      onCancel={(event) => {
        // Esc: отменяем встроенное закрытие, чтобы состоянием управлял React.
        event.preventDefault();
        if (dismissible) onClose();
      }}
      onMouseDown={(event) => {
        // Клик по подложке. Сравнение с currentTarget обязательно: при клике
        // внутри карточки событие всплывает, и без проверки окно закрывалось бы.
        if (dismissible && event.target === event.currentTarget) onClose();
      }}
    >
      <div className={styles.core}>
        {dismissible ? (
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <XIcon size={18} weight="bold" aria-hidden="true" />
          </button>
        ) : null}
        {children}
      </div>
    </dialog>
  );
}
